import { useEffect, useState } from "react";
import { shallow } from "zustand/shallow";
import axios from "axios";

import useAppStore from "@duck/app-store";
import { hasActiveViewerLink } from "./assessmentViewerAccess";
import { hasPermission, PERMISSIONS } from "./permissions";

const initialLookup = {
  loading: false,
  linked: false,
  error: null,
  assessmentId: null,
};

/**
 * Presentation-only access state for assessment results.
 * The assessment result and response APIs remain the security boundary.
 *
 * @param {{
 *   assessment?: { id?: string|number }|null,
 *   organizationId?: string|number|null,
 * }} options
 */
export default function useAssessmentViewerAccess({
  assessment,
  organizationId,
}) {
  const { admin, user, permissions } = useAppStore(
    (state) => ({
      admin: state.admin,
      user: state.user,
      permissions: state.permissions,
    }),
    shallow
  );
  const [lookup, setLookup] = useState(initialLookup);

  const canEvaluate = hasPermission(
    permissions,
    PERMISSIONS.ASSESSMENTS_EVALUATE
  );
  const canViewAssessments = hasPermission(
    permissions,
    PERMISSIONS.ASSESSMENTS_VIEW
  );
  const actorEmail = admin?.email || user?.email;
  const assessmentKey = String(assessment?.id || "");
  const shouldLookup =
    Boolean(assessmentKey && organizationId && canViewAssessments && !canEvaluate);

  useEffect(() => {
    let cancelled = false;

    if (!shouldLookup) {
      setLookup(initialLookup);
      return () => {
        cancelled = true;
      };
    }

    setLookup({
      loading: true,
      linked: false,
      error: null,
      assessmentId: assessmentKey,
    });

    axios
      .post(
        `/organizations/${organizationId}/retrieve-assessment-viewer-links/`,
        {
          assessment_id: assessment.id,
          page: 0,
          page_size: 50,
        }
      )
      .then(({ data }) => {
        if (cancelled) return;

        const links = data.response?.[0]?.data || [];
        const linked = hasActiveViewerLink(links, actorEmail);
        setLookup({
          loading: false,
          linked,
          error: null,
          assessmentId: assessmentKey,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setLookup({
          loading: false,
          linked: false,
          error,
          assessmentId: assessmentKey,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    actorEmail,
    assessment?.id,
    assessmentKey,
    canEvaluate,
    canViewAssessments,
    organizationId,
    shouldLookup,
  ]);

  const currentLookup = lookup.assessmentId === assessmentKey ? lookup : initialLookup;
  const viewerLookupLoading =
    shouldLookup && (currentLookup.loading || !currentLookup.assessmentId);
  const viewerLinked = shouldLookup && currentLookup.linked;

  return {
    canEvaluate,
    canViewAssessments,
    viewerLinked,
    viewerLookupLoading,
    viewerLookupError: shouldLookup ? currentLookup.error : null,
    canViewResults: canEvaluate || (!viewerLookupLoading && viewerLinked),
  };
}
